import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { sequenceSchema, SequenceFormData } from '../sequences/upload/types';
import { SpotifyTrack } from '@/services/spotifyService';
import { enhancedSpotifyService } from '@/services/enhancedSpotifyService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { filterSequenceData } from '@/utils/sequenceDataFilter';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Save, Music, CheckCircle, ArrowLeft, ChevronsUpDown, ExternalLink, AlertTriangle, Check, Camera } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import SpotifyTrackSelector from '@/components/sequences/SpotifyTrackSelector';
import LayoutPhotoUpload, { LayoutPhotoFile } from '@/components/sequences/upload/LayoutPhotoUpload';
import { layoutPhotoService } from '@/services/layoutPhotoService';

interface UserSequenceEditorProps {
  sequenceId: string;
}

export function UserSequenceEditor({ sequenceId }: UserSequenceEditorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sequence, setSequence] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSequence = async () => {
      try {
        setLoading(true);
        const { data: sequenceData, error } = await supabase
          .from('sequences')
          .select(`
            *,
            artists(name),
            genres(name)
          `)
          .eq('id', sequenceId)
          .single();

        if (error) throw error;

        if (sequenceData) {
          setSequence(sequenceData);
          setFormData({
            name: sequenceData.name || '',
            title: sequenceData.title || '',
            artist: sequenceData.artists?.name || '',
            artist_id: sequenceData.artist_id || '',
            description: sequenceData.description || '',
            software_type: sequenceData.software_type || 'xLights',
            holiday_type: sequenceData.holiday_type || 'Christmas',
            file_type: sequenceData.file_type || 'Sequence only',
            genre: sequenceData.genres?.name || '',
            genre_id: sequenceData.genre_id || '',
            price: sequenceData.price || 0,
            is_free: sequenceData.is_free || false,
            display_id: sequenceData.display_id || '',
            album: sequenceData.album || '',
            album_cover_url: sequenceData.album_cover_url || '',
            image_url: sequenceData.image_url || '',
            video_url: sequenceData.video_url || '',
            duration: sequenceData.duration || 0,
            spotify_track_id: sequenceData.spotify_track_id || '',
            spotify_preview_url: sequenceData.spotify_preview_url || '',
            spotify_explicit: sequenceData.spotify_explicit || false,
            spotify_duration_ms: sequenceData.spotify_duration_ms || 0,
            spotify_popularity: sequenceData.spotify_popularity || 0,
          });

          // Load layout photos
          try {
            setPhotosLoading(true);
            const existingPhotos = await layoutPhotoService.getLayoutPhotos(sequenceId);
            const photosWithPreviews: LayoutPhotoFile[] = existingPhotos.map((photo, index) => ({
              file: new File([], `photo-${index}`, { type: 'image/jpeg' }), // Placeholder file
              preview: photo.photo_url,
              caption: photo.caption || '',
              id: photo.id,
            }));
            setLayoutPhotos(photosWithPreviews);
          } catch (photoError) {
            console.error('Error loading layout photos:', photoError);
            toast.error('Failed to load layout photos');
          } finally {
            setPhotosLoading(false);
          }
        }
      } catch (error) {
        console.error('Error fetching sequence:', error);
        toast.error('Failed to load sequence');
      } finally {
        setLoading(false);
      }
    };

    fetchSequence();
  }, [sequenceId]);

  const [formData, setFormData] = useState<any>({
    name: '',
    title: '',
    artist: '',
    artist_id: '',
    description: '',
    software_type: 'xLights',
    holiday_type: 'Christmas',
    file_type: 'Sequence only',
    genre: '',
    genre_id: '',
    price: 0,
    is_free: false,
    display_id: '',
    album: '',
    album_cover_url: '',
    image_url: '',
    video_url: '',
    duration: 0,
    spotify_track_id: '',
    spotify_preview_url: '',
    spotify_explicit: false,
    spotify_duration_ms: 0,
    spotify_popularity: 0,
  });

  const [spotifyData, setSpotifyData] = useState<SpotifyTrack | null>(null);
  const [saving, setSaving] = useState(false);
  const [artists, setArtists] = useState<any[]>([]);
  const [genres, setGenres] = useState<any[]>([]);
  const [displays, setDisplays] = useState<any[]>([]);
  const [genreOpen, setGenreOpen] = useState(false);
  const [layoutPhotos, setLayoutPhotos] = useState<LayoutPhotoFile[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  const form = useForm<SequenceFormData>({
    resolver: zodResolver(sequenceSchema),
    defaultValues: formData,
  });

  // Load artists, genres, and displays for form dropdowns
  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [artistsRes, genresRes, displaysRes] = await Promise.all([
          supabase.from('artists').select('id, name').order('name'),
          supabase.from('genres').select('id, name').order('name'),
          supabase.from('displays').select('id, name').eq('user_id', user?.id || '').order('name')
        ]);

        if (artistsRes.data) setArtists(artistsRes.data);
        if (genresRes.data) setGenres(genresRes.data);
        if (displaysRes.data) setDisplays(displaysRes.data);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };

    if (user?.id) {
      loadFormData();
    }
  }, [user?.id]);

  // Enhanced genre assignment using the enhanced Spotify service
  const assignGenreFromSpotify = async (track: SpotifyTrack): Promise<void> => {
    try {
      console.log('[Genre Assignment] Starting enhanced genre detection for track:', track.name);
      
      const detection = await enhancedSpotifyService.detectGenre(track);
      
      if (detection) {
        console.log('[Genre Assignment] Enhanced detection result:', detection);
        
        setFormData((prev: any) => ({ 
          ...prev, 
          genre_id: detection.genreId,
          genre: detection.genreName
        }));
        
        toast.success(`Genre "${detection.genreName}" automatically assigned (${detection.source})`);
      } else {
        console.log('[Genre Assignment] No genre detected, using fallback');
        
        // Fallback to basic detection
        const fallbackGenre = detectGenreFallback(track);
        if (fallbackGenre) {
          setFormData((prev: any) => ({ 
            ...prev, 
            genre: fallbackGenre
          }));
          toast.success(`Genre "${fallbackGenre}" assigned using fallback detection`);
        } else {
          toast.info('No genre could be automatically detected');
        }
      }
    } catch (error) {
      console.error('[Genre Assignment] Error in enhanced genre detection:', error);
      toast.error('Failed to assign genre automatically');
    }
  };

  const detectGenreFallback = (track: SpotifyTrack): string => {
    const title = track.name.toLowerCase();
    const artist = track.artists[0]?.name.toLowerCase() || '';
    const album = track.album.name.toLowerCase();
    
    // Basic fallback detection
    if (title.includes('christmas') || title.includes('xmas') || title.includes('holiday') || 
        album.includes('christmas') || album.includes('holiday')) {
      return 'Christmas';
    }
    
    if (title.includes('rock') || artist.includes('rock') || album.includes('rock')) {
      return 'Rock';
    }
    
    if (title.includes('pop') || artist.includes('pop') || album.includes('pop')) {
      return 'Pop';
    }
    
    if (title.includes('country') || artist.includes('country') || album.includes('country')) {
      return 'Country';
    }
    
    if (title.includes('jazz') || artist.includes('jazz') || album.includes('jazz')) {
      return 'Jazz';
    }
    
    if (title.includes('classical') || artist.includes('classical') || album.includes('classical')) {
      return 'Classical';
    }
    
    return 'Contemporary';
  };

  const handleSpotifySelect = async (track: SpotifyTrack) => {
    setSpotifyData(track);
    
    // Auto-fill form data from Spotify - including album cover and metadata
    setFormData((prev: any) => ({
      ...prev,
      title: track.name,
      duration: Math.round(track.duration_ms / 1000),
      // Add all the missing Spotify metadata fields
      album: track.album.name,
      album_cover_url: track.album.images.find(img => img.width === 640)?.url || track.album.images[0]?.url,
      spotify_track_id: track.id,
      spotify_explicit: track.explicit,
      spotify_preview_url: track.preview_url,
      spotify_duration_ms: track.duration_ms,
      spotify_popularity: track.popularity,
    }));

    // Try to find or create artist and auto-assign genre
    if (track.artists && track.artists.length > 0) {
      const artistName = track.artists[0].name;
      const artistId = track.artists[0].id;
      
      try {
        // Check if artist exists
        const { data: existingArtist } = await supabase
          .from('artists')
          .select('id, name')
          .ilike('name', artistName)
          .single();

        if (existingArtist) {
          setFormData((prev: any) => ({ ...prev, artist_id: existingArtist.id }));
        } else {
          // Create new artist
          const { data: newArtist, error } = await supabase
            .from('artists')
            .insert({
              name: artistName,
              description: `Artist automatically created from Spotify: ${artistName}`
            })
            .select('id, name')
            .single();

          if (!error && newArtist) {
            setFormData((prev: any) => ({ ...prev, artist_id: newArtist.id }));
            setArtists(prev => [...prev, newArtist]);
            toast.success(`Artist "${artistName}" created successfully`);
          }
        }

        // Enhanced genre assignment
        await assignGenreFromSpotify(track);
        
      } catch (error) {
        console.error('Error processing Spotify data:', error);
        toast.error('Failed to process Spotify data');
      }
    }
  };

  // Handle layout photo changes
  const handleLayoutPhotosChange = (photos: LayoutPhotoFile[]) => {
    setLayoutPhotos(photos);
  };

  // Authentication and sequence loading state handled above

  const handleSave = async () => {
    if (!sequence) return;

    setSaving(true);
    try {
      // Use the safe filtering function to ensure only valid database columns are included
      const filteredUpdates = filterSequenceData({
        ...formData,
        updated_at: new Date().toISOString(),
      });

      // Log what genre_id is being saved for debugging
      console.log(`[Save Sequence] Current form data genre_id: ${formData.genre_id}`);
      console.log(`[Save Sequence] Filtered data being saved:`, filteredUpdates);
      console.log(`[Save Sequence] Genre ID in filtered data: ${filteredUpdates.genre_id}`);

      const { error } = await supabase
        .from('sequences')
        .update(filteredUpdates)
        .eq('id', sequenceId)
        .eq('user_id', user.id); // Double-check ownership

      if (error) throw error;

      // Handle LOR metadata if software type is Light-O-Rama
      if (formData.software_type === 'Light-O-Rama' && (formData as any).lor_metadata) {
        try {
          const lorMetadata = (formData as any).lor_metadata;
          
          // Check if LOR metadata already exists
          const { data: existingLorMeta } = await supabase
            .from('sequence_lor_meta')
            .select('id')
            .eq('sequence_id', sequenceId)
            .single();

          if (existingLorMeta) {
            // Update existing LOR metadata
            const { error: lorError } = await supabase
              .from('sequence_lor_meta')
              .update({
                controller_type: lorMetadata.controller_type,
                channel_count_ac: lorMetadata.channel_count_ac,
                software_version: lorMetadata.software_version,
                prop_types: lorMetadata.prop_types || [],
                license_level_required: lorMetadata.license_level_required,
                sequence_type_tags: lorMetadata.sequence_type_tags || [],
                import_notes: lorMetadata.import_notes || null,
                updated_at: new Date().toISOString()
              })
              .eq('sequence_id', sequenceId);

            if (lorError) {
              console.error('Failed to update LOR metadata:', lorError);
            } else {
              console.log('✅ LOR metadata updated successfully');
            }
          } else {
            // Create new LOR metadata
            const { error: lorError } = await supabase
              .from('sequence_lor_meta')
              .insert({
                sequence_id: sequenceId,
                controller_type: lorMetadata.controller_type,
                channel_count_ac: lorMetadata.channel_count_ac,
                software_version: lorMetadata.software_version,
                prop_types: lorMetadata.prop_types || [],
                license_level_required: lorMetadata.license_level_required,
                sequence_type_tags: lorMetadata.sequence_type_tags || [],
                import_notes: lorMetadata.import_notes || null
              });

            if (lorError) {
              console.error('Failed to create LOR metadata:', lorError);
            } else {
              console.log('✅ LOR metadata created successfully');
            }
          }
        } catch (error) {
          console.warn('Error handling LOR metadata:', error);
          // Don't fail the sequence update if LOR metadata handling fails
        }
      }

      // Handle layout photos
      try {
        // Get existing photos to compare
        const existingPhotos = await layoutPhotoService.getLayoutPhotos(sequenceId);
        const existingPhotoIds = new Set(existingPhotos.map(p => p.id));

        // Find new photos that need to be uploaded
        const newPhotos = layoutPhotos.filter(photo =>
          !existingPhotoIds.has(photo.id) && photo.file.size > 0
        );

        // Upload new photos
        if (newPhotos.length > 0) {
          const photosToUpload = newPhotos.map(photo => ({
            file: photo.file,
            caption: photo.caption
          }));
          await layoutPhotoService.uploadLayoutPhotos(sequenceId, photosToUpload);
        }

        // Update captions for existing photos
        for (const photo of layoutPhotos) {
          if (existingPhotoIds.has(photo.id)) {
            const existingPhoto = existingPhotos.find(p => p.id === photo.id);
            if (existingPhoto && existingPhoto.caption !== photo.caption) {
              await layoutPhotoService.updateCaption(photo.id, photo.caption || '');
            }
          }
        }

        // Remove photos that were deleted
        const currentPhotoIds = new Set(layoutPhotos.map(p => p.id));
        for (const existingPhoto of existingPhotos) {
          if (!currentPhotoIds.has(existingPhoto.id)) {
            await layoutPhotoService.deleteLayoutPhoto(existingPhoto.id);
          }
        }
      } catch (photoError) {
        console.error('Error saving layout photos:', photoError);
        toast.error('Failed to save layout photos');
        return; // Don't continue if photo save fails
      }

      console.log(`[Save Sequence] Successfully saved sequence ${sequenceId} with genre_id: ${filteredUpdates.genre_id}`);
      toast.success('Sequence updated successfully!');
      navigate('/my-sequences');
    } catch (err) {
      console.error('Error updating sequence:', err);
      toast.error('Failed to update sequence');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/my-sequences');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading sequence...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!sequence) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Sequence Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The sequence you're looking for doesn't exist or you don't have permission to edit it.
          </p>
          <Button onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My Sequences
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="ghost" 
              onClick={handleCancel}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to My Sequences
            </Button>
            <h1 className="text-3xl font-bold">Edit Sequence</h1>
            <p className="text-muted-foreground">
              Update your sequence details, pricing, and metadata
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Sequence Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter sequence name"
                />
              </div>

              <div>
                <Label htmlFor="title">Display Title</Label>
                <Input
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Title for display (optional)"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your sequence..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="software_type">Software Type</Label>
                <Select
                  value={formData.software_type || undefined}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, software_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select software" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xLights">xLights</SelectItem>
                    <SelectItem value="Light-O-Rama">Light-O-Rama</SelectItem>
                    <SelectItem value="Vixen">Vixen</SelectItem>
                    <SelectItem value="HLS">HLS</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="duration">Duration (seconds)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                  placeholder="Duration in seconds"
                />
              </div>

              <div>
                <Label htmlFor="holiday_type">Holiday Type</Label>
                <Select
                  value={formData.holiday_type || undefined}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, holiday_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select holiday type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Christmas">Christmas</SelectItem>
                    <SelectItem value="Halloween">Halloween</SelectItem>
                    <SelectItem value="FourthOfJuly">4th of July</SelectItem>
                    <SelectItem value="Easter">Easter</SelectItem>
                    <SelectItem value="Thanksgiving">Thanksgiving</SelectItem>
                    <SelectItem value="Hanukkah">Hanukkah</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="file_type">File Type</Label>
                <Select
                  value={formData.file_type || undefined}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, file_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select file type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sequence only">Sequence only</SelectItem>
                    <SelectItem value="Lyrics only">Lyrics only</SelectItem>
                    <SelectItem value="Sequence & Lyrics">Sequence & Lyrics</SelectItem>
                    <SelectItem value="Singing Face">Singing Face</SelectItem>
                    <SelectItem value="Matrix Video">Matrix Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Artist & Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Artist & Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="artist">Artist</Label>
                <Select
                  value={formData.artist_id || undefined}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, artist_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select artist" />
                  </SelectTrigger>
                  <SelectContent>
                    {artists.map((artist) => (
                      <SelectItem key={artist.id} value={artist.id}>
                        {artist.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="genre">Genre</Label>
                <Popover open={genreOpen} onOpenChange={setGenreOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={genreOpen}
                      className="w-full justify-between"
                    >
                      {formData.genre_id 
                        ? genres.find(g => g.id === formData.genre_id)?.name 
                        : "Select genre..."
                      }
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Search genre..." />
                      <CommandEmpty>No genre found.</CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          {genres
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((genre) => (
                            <CommandItem
                              key={genre.id}
                              value={genre.name}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, genre_id: genre.id }));
                                setGenreOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.genre_id === genre.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {genre.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_free"
                  checked={formData.is_free || false}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    is_free: e.target.checked,
                    price: e.target.checked ? 0 : prev.price 
                  }))}
                  className="rounded"
                />
                <Label htmlFor="is_free">Free sequence</Label>
              </div>

              {!formData.is_free && (
                <div>
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
              )}

              <Separator />

              {/* Spotify Integration */}
              <div>
                <Label>Spotify Integration</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Search for the song on Spotify to auto-fill metadata and artist information.
                </p>
                <SpotifyTrackSelector onTrackSelect={handleSpotifySelect} />
                
                {spotifyData && (
                  <Alert className="mt-4">
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      <span className="font-medium">Spotify track selected:</span><br />
                      {spotifyData.name} by {spotifyData.artists?.[0]?.name}
                      {spotifyData.external_urls?.spotify && (
                        <a
                          href={spotifyData.external_urls.spotify}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center ml-2 text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Separator />

              {/* Media URLs */}
              <div>
                <Label className="text-base font-medium">Media URLs</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Add custom image and video URLs for your sequence
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="image_url">Image URL</Label>
                    <Input
                      id="image_url"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="video_url">Video URL</Label>
                    <Input
                      id="video_url"
                      value={formData.video_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Layout Photos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Layout Photos
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Upload photos of your light display layout to help users visualize the sequence
            </p>
          </CardHeader>
          <CardContent>
            {photosLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Loading photos...</p>
              </div>
            ) : (
              <LayoutPhotoUpload
                photos={layoutPhotos}
                onPhotosChange={handleLayoutPhotosChange}
                maxPhotos={3}
                disabled={saving}
              />
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !formData.name}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Current File Info */}
        {sequence && (
          <Card>
            <CardHeader>
              <CardTitle>Current File</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{sequence.name}</p>
                  {sequence.file_url && (
                    <p className="text-sm text-muted-foreground">
                      File uploaded: {new Date(sequence.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                <Alert className="max-w-md">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    To update the sequence file, please use the upload page or contact support.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}